import { inject, Signal } from '@angular/core';

import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import {
  ContentFilters,
  ExportSchedule,
} from 'src/app/core/models/core.models';
import { FormMode } from 'src/app/core/models/form.models';
import {
  augmentObjectWithIconAndImage,
  augmentObjects,
} from 'src/app/shared/utils/object-utils';
import { ExportScheduleApiService } from '../services/export-schedule-api.service';
import { UtilsDataObjectApiService } from '../services/utils-data-object-api.service';

export interface ExportSchedulesScope {
  objectId: string;
  objectClassId: number;
}

const EXPORT_SCHEDULES_ROOT_KEY = [
  'export_schedule',
  'get_object_export_schedules',
];

export const exportSchedulesQueryKey = (scope: ExportSchedulesScope | null) => [
  ...EXPORT_SCHEDULES_ROOT_KEY,
  {
    objectId: scope?.objectId ?? null,
    objectClassId: scope?.objectClassId ?? null,
  },
];

/**
 * Export schedules of an object (GET /export_schedule/get_object_export_schedules).
 * The scope is a signal - the panel learns the object from its inputs and then from
 * `objectChanged`, so the query stays disabled until it is set.
 *
 * The feed/dashboard badge count is **not** derived from this list: it comes from
 * `object.count_export_schedules` and stays NgRx, so callers must keep broadcasting
 * `createExportScheduleSuccess` / `deleteExportScheduleSuccess`.
 */
export function injectExportSchedulesQuery(
  scope: Signal<ExportSchedulesScope | null>,
) {
  const exportScheduleApi = inject(ExportScheduleApiService);

  return injectQuery(() => ({
    queryKey: exportSchedulesQueryKey(scope()),
    queryFn: () =>
      firstValueFrom(
        exportScheduleApi.getExportSchedules(
          scope().objectId,
          scope().objectClassId,
        ),
      ).then((exportSchedules) => augmentObjects(exportSchedules ?? [])),
    enabled: !!scope(),
  }));
}

export interface SaveExportScheduleInput {
  objectId: string;
  objectClassId: number;
  postIds: string[];
  contentFilters: ContentFilters;
  settings: Record<string, any>;
  formMode: FormMode;
}

/**
 * Create or update an export schedule (POST /utils_data_object/schedule).
 *
 * `onSuccess` patches every cached list and *then* invalidates: the server
 * recalculates `next_scheduled_time`, which a local patch cannot know.
 */
export function injectSaveExportScheduleMutation() {
  const utilsDataObjectApi = inject(UtilsDataObjectApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      objectId,
      objectClassId,
      postIds,
      contentFilters,
      settings,
    }: SaveExportScheduleInput) =>
      firstValueFrom(
        utilsDataObjectApi.schedule(
          objectId,
          objectClassId,
          postIds,
          contentFilters,
          settings,
        ),
      ),
    onSuccess: (exportSchedule, { formMode }) => {
      const isCreated = formMode === FormMode.Create;

      queryClient.setQueriesData<ExportSchedule[]>(
        { queryKey: EXPORT_SCHEDULES_ROOT_KEY },
        (list) => upsertExportSchedule(list, exportSchedule, isCreated),
      );
      queryClient.invalidateQueries({ queryKey: EXPORT_SCHEDULES_ROOT_KEY });

      toast.showSuccess(
        translate.instant(
          isCreated
            ? 'msg.export.export-scheduled'
            : 'msg.export.export-schedule-updated',
        ),
      );
    },
    onError: (error: any) =>
      toast.showError(
        translate.instant('msg.export.export-schedule-update-failed', {
          error: error?.message || error,
        }),
      ),
  }));
}

function upsertExportSchedule(
  list: ExportSchedule[] | undefined,
  saved: ExportSchedule,
  isCreated: boolean,
): ExportSchedule[] | undefined {
  if (!list || !saved) {
    return list;
  }

  const entry = augmentObjectWithIconAndImage(saved);

  if (isCreated) {
    return [...list, entry];
  }

  return list.map((item) => (item.id === saved.id ? entry : item));
}

/**
 * Pause/resume an export schedule (POST /export_schedule/:id/toggle_status).
 * Invalidated rather than patched locally: the backend also recalculates
 * `next_scheduled_time` when it flips the status. The toast is a hardcoded English
 * string - no translation key exists for it.
 */
export function injectToggleExportScheduleStatusMutation() {
  const exportScheduleApi = inject(ExportScheduleApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);

  return injectMutation(() => ({
    mutationFn: (exportScheduleId: string) =>
      firstValueFrom(
        exportScheduleApi.toggleExportScheduleStatus(exportScheduleId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPORT_SCHEDULES_ROOT_KEY });
      toast.showSuccess('Export Schedule Status Changed Successfully');
    },
  }));
}

/**
 * Delete an export schedule (DELETE /export_schedule/:id/delete). No toast on
 * either outcome, so a failed delete is silent today.
 */
export function injectDeleteExportScheduleMutation() {
  const exportScheduleApi = inject(ExportScheduleApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (exportScheduleId: string) =>
      firstValueFrom(exportScheduleApi.deleteExportSchedule(exportScheduleId)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: EXPORT_SCHEDULES_ROOT_KEY }),
  }));
}
